import { useMemo, useState } from "react";

import { api, ApiFailure } from "../api/client";
import { failureMessage } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import type { AMDConfig, VoiceApp } from "../api/types";
import { useSession } from "../store/session";
import { MARKUP_EXAMPLES, MarkupEditor } from "../components/MarkupEditor";
import { ActionError, Badge, Button, Card, Confirm, Field, Modal, PageHeader, inputClass } from "../components/ui";

export function VoiceApps() {
  const can = useSession((s) => s.can);
  const lista = usePagedResource<VoiceApp>("/api/v1/voice-apps");
  const apps = lista.data;

  const [creating, setCreating] = useState(false);
  const [editando, setEditando] = useState<VoiceApp | null>(null);
  const [borrando, setBorrando] = useState<VoiceApp | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function eliminar(app: VoiceApp) {
    setErrorAccion(null);
    setOcupado(true);
    try {
      await api.delete(`/api/v1/voice-apps/${app.id}`);
      setBorrando(null);
      await lista.reload(true);
    } catch (err) {
      // El 409 de «sigue asignada a una ruta entrante» es el caso frecuente, y
      // el mensaje de la API ya lo explica: se muestra tal cual en lugar de
      // uno genérico.
      setErrorAccion(failureMessage(err, "no se pudo eliminar la aplicación"));
      setBorrando(null);
    } finally {
      setOcupado(false);
    }
  }

  const columnas = useMemo<ColumnDef<VoiceApp, any>[]>(
    () => [
      {
        id: "name",
        accessorFn: (app) => `${app.name} ${app.voice_url ?? ""}`,
        header: "Nombre",
        meta: { label: "Nombre" },
        cell: ({ row }) => (
          <>
            <div className="font-medium text-slate-100">{row.original.name}</div>
            <div className="font-mono text-xs text-slate-500">{row.original.id}</div>
          </>
        ),
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Instrucciones",
        meta: { label: "Instrucciones" },
        cell: ({ row }) =>
          row.original.source === "markup" ? (
            <div>
              <Badge tone="good">Markup guardado</Badge>
              <div className="mt-1 max-w-md truncate font-mono text-xs text-slate-500">
                {resumenDeMarkup(row.original.markup)}
              </div>
            </div>
          ) : (
            <div className="max-w-md truncate font-mono text-xs text-slate-400">
              {row.original.voice_url}
            </div>
          ),
      },
      {
        // El método y el tiempo de espera solo significan algo cuando hay una
        // petición HTTP de por medio.
        id: "voice_method",
        accessorKey: "voice_method",
        header: "Método",
        meta: { label: "Método", hideOnMobile: true, width: "6rem" },
        cell: ({ row }) => (
          <span className="text-slate-600">
            {row.original.source === "markup" ? "—" : row.original.voice_method}
          </span>
        ),
      },
      {
        id: "timeout_ms",
        accessorKey: "timeout_ms",
        header: "Timeout",
        meta: { label: "Timeout", align: "right", hideOnMobile: true, width: "7rem" },
        cell: ({ row }) => (
          <span className="text-slate-600">
            {row.original.source === "markup" ? "—" : `${row.original.timeout_ms} ms`}
          </span>
        ),
      },
      {
        // Dos cosas que se consultan juntas: si avisa, y si graba.
        id: "status_callback_url",
        accessorKey: "status_callback_url",
        header: "Avisos",
        meta: { label: "Avisos", hideOnMobile: true },
        cell: ({ row }) => (
          <>
            {row.original.status_callback_url === null ? (
              <span className="text-xs text-slate-600">sin webhook</span>
            ) : (
              <div
                className="max-w-[14rem] truncate font-mono text-xs text-slate-400"
                title={row.original.status_callback_url}
              >
                {row.original.status_callback_url}
              </div>
            )}
            {row.original.recording_enabled && (
              <Badge tone="warn" className="mt-1">
                graba
              </Badge>
            )}
          </>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "7rem" },
        cell: ({ row }) => (
          <Badge tone={row.original.status === "active" ? "good" : "neutral"}>
            {row.original.status}
          </Badge>
        ),
      },
    ],
    [],
  );

  const acciones = useMemo<DataTableRowAction<VoiceApp>[]>(
    () =>
      can("voice_apps.manage")
        ? [
            { label: "Editar", onSelect: (app) => setEditando(app) },
            { label: "Eliminar", tone: "danger", onSelect: (app) => setBorrando(app) },
          ]
        : [],
    [can],
  );

  return (
    <div>
      <PageHeader
        title="Aplicaciones de voz"
        description="Lo que hace la plataforma cuando contesta una llamada. Puede ser markup guardado aquí —se ejecuta sin salir a la red, sin que tengas servidor— o una Voice URL tuya que se consulta en cada llamada. Apuntas varios números a la misma y la cambias en un solo sitio."
        action={
          can("voice_apps.manage") && (
            <Button onClick={() => setCreating(true)}>Nueva aplicación</Button>
          )
        }
      />

      {creating && (
        <CreateForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void lista.reload(true);
          }}
        />
      )}

      {editando !== null && (
        <EditForm
          app={editando}
          onCancel={() => setEditando(null)}
          onSaved={() => {
            setEditando(null);
            void lista.reload(true);
          }}
        />
      )}

      {borrando !== null && (
        <Confirm
          title={`¿Eliminar «${borrando.name}»?`}
          description={
            <>
              Los números que apunten a esta aplicación se quedan sin destino. Si alguno la
              tiene asignada, la plataforma lo rechaza y te lo dice.
              <span className="mt-2 block">No se puede deshacer.</span>
            </>
          }
          confirmLabel="Eliminar"
          busy={ocupado}
          onConfirm={() => void eliminar(borrando)}
          onCancel={() => setBorrando(null)}
        />
      )}

      {errorAccion !== null && <ActionError message={errorAccion} />}

      <DataTable<VoiceApp>
        data={apps}
        columns={columnas}
        getRowId={(app) => app.id}
        loading={lista.loading}
        error={lista.error}
        onRetry={() => void lista.reload(false)}
        caption="Aplicaciones de voz"
        empty={{
          title: "Sin aplicaciones de voz",
          description: "Una aplicación es la instrucción que la plataforma ejecuta al contestar.",
        }}
        search={{ placeholder: "Buscar por nombre o URL…" }}
        filters={[
          {
            columnId: "source",
            label: "Instrucciones",
            options: [
              { value: "markup", label: "Markup guardado" },
              { value: "voice_url", label: "Voice URL" },
            ],
          },
          {
            columnId: "status",
            label: "Estado",
            options: [
              { value: "active", label: "Activa" },
              { value: "disabled", label: "Deshabilitada" },
            ],
          },
        ]}
        rowActions={acciones}
        hideableColumns
        urlStateKey="apps"
        pagination={{
          pageSize: lista.enEstaPagina,
          hasNext: lista.hayMas,
          hasPrevious: lista.hayAnterior,
          onNext: lista.siguiente,
          onPrevious: lista.anterior,
        }}
      />

      <MarkupScratchpad />
    </div>
  );
}

/**
 * MarkupScratchpad valida un documento sin tener que originar una llamada.
 *
 * Hace falta porque una voice app guarda una **URL**, no markup: lo que el
 * tenant necesita comprobar es que lo que su servidor va a devolver parsea y
 * se puede ejecutar, y descubrirlo en una llamada de verdad cuesta una llamada
 * y un prospecto.
 */
function MarkupScratchpad() {
  const [markup, setMarkup] = useState<string>(MARKUP_EXAMPLES[0]?.markup ?? "");

  return (
    <section className="mt-8">
      <h2 className="text-sm font-medium text-slate-100">Probar un documento</h2>
      <p className="mt-1 mb-3 max-w-prose text-xs text-slate-500">
        Se valida con la misma gramática que ejecuta las llamadas. Sirve para
        comprobar lo que tu servidor va a devolver antes de que lo devuelva.
      </p>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap gap-2">
          {MARKUP_EXAMPLES.map((example) => (
            <Button
              key={example.name}
              type="button"
              variant="secondary"
              onClick={() => setMarkup(example.markup)}
            >
              {example.name}
            </Button>
          ))}
        </div>
        <MarkupEditor value={markup} onChange={setMarkup} rows={10} />
      </Card>
    </section>
  );
}

function CreateForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  // Arranca en markup porque es el caso que no necesita nada más: se escribe
  // aquí y funciona. La Voice URL exige tener un servidor en pie.
  const [fuente, setFuente] = useState<"markup" | "url">("markup");
  const [markupDoc, setMarkupDoc] = useState(MARKUP_EXAMPLES[0]?.markup ?? "");
  const [voiceURL, setVoiceURL] = useState("");
  const [callbackURL, setCallbackURL] = useState("");
  const [grabar, setGrabar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setField(null);
    try {
      await api.post("/api/v1/voice-apps", {
        name,
        ...(fuente === "markup" ? { markup: markupDoc } : { voice_url: voiceURL }),
        ...(callbackURL.trim() === "" ? {} : { status_callback_url: callbackURL.trim() }),
        recording_enabled: grabar,
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiFailure) {
        setError(err.error.message);
        // El backend indica qué campo falló; se resalta en lugar de dejar al
        // usuario adivinar.
        setField((err.error.details?.field as string) ?? null);
      } else {
        setError("error inesperado");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Nueva aplicación de voz"
      description="Puedes cambiar todo después; nada de esto es definitivo."
      width="lg"
      onClose={onCancel}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Nombre" error={field === "name" ? error ?? undefined : undefined}>
          <input
            required
            maxLength={120}
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Campaña BDC"
          />
        </Field>

        <fieldset>
          <legend className="text-xs font-medium uppercase tracking-wider text-slate-400">
            De dónde salen las instrucciones
          </legend>
          <div className="mt-2 space-y-1">
            <label className="flex items-start gap-2 text-sm text-slate-300">
              <input
                type="radio"
                className="mt-1"
                checked={fuente === "markup"}
                onChange={() => setFuente("markup")}
              />
              <span>
                Markup guardado aquí
                <span className="block text-xs text-slate-500">
                  La plataforma ejecuta este documento tal cual. No necesitas servidor HTTP, no
                  hay latencia de red y no hay nada que se caiga del otro lado.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-300">
              <input
                type="radio"
                className="mt-1"
                checked={fuente === "url"}
                onChange={() => setFuente("url")}
              />
              <span>
                Una Voice URL tuya
                <span className="block text-xs text-slate-500">
                  La plataforma consulta tu servidor en cada llamada. Hace falta cuando la
                  respuesta depende de quién llama.
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {fuente === "markup" ? (
          <div>
            <div className="mb-2 flex flex-wrap gap-2">
              {MARKUP_EXAMPLES.map((ejemplo) => (
                <button
                  key={ejemplo.name}
                  type="button"
                  className="rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-slate-300 transition hover:bg-white/[0.09] hover:text-white"
                  onClick={() => setMarkupDoc(ejemplo.markup)}
                >
                  {ejemplo.name}
                </button>
              ))}
            </div>
            <MarkupEditor value={markupDoc} onChange={setMarkupDoc} rows={10} />
            {field === "markup" && error && (
              <p className="mt-2 text-xs text-rose-300">{error}</p>
            )}
          </div>
        ) : (
          <Field
            label="Voice URL"
            hint="Debe ser https y resolver a una dirección pública. Las direcciones internas se rechazan."
            error={field === "voice_url" ? error ?? undefined : undefined}
          >
            <input
              required
              type="url"
              className={inputClass}
              value={voiceURL}
              onChange={(e) => setVoiceURL(e.target.value)}
              placeholder="https://tu-servidor.com/voz"
            />
          </Field>
        )}

        <div className="border-t border-white/10 pt-4">
          <Field
            label="Avisar a esta URL (opcional)"
            hint="Cuando una llamada de esta aplicación cambia de estado, llega aquí un POST firmado. Se puede poner después."
            error={field === "status_callback_url" ? error ?? undefined : undefined}
          >
            <input
              type="url"
              className={inputClass}
              value={callbackURL}
              onChange={(e) => setCallbackURL(e.target.value)}
              placeholder="https://tu-servidor.com/eventos"
            />
          </Field>

          <label className="mt-4 flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={grabar}
              onChange={(e) => setGrabar(e.target.checked)}
            />
            <span className="text-sm text-slate-300">
              <strong className="text-slate-100">Grabar las llamadas</strong>
              <span className="mt-1 block text-xs text-slate-500">
                El aviso de fin de llamada llevará la liga de la grabación, firmada y con
                caducidad. El aviso a la otra parte lo das tú.
              </span>
            </span>
          </label>
        </div>

        {error && !field && (
          <p className="rounded-md bg-rose-500/10 px-3 py-2 text-sm text-rose-200" role="alert">
            {error}
          </p>
        )}

        <div className="flex gap-2 border-t border-white/10 pt-4">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creando…" : "Crear"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * resumenDeMarkup describe el documento en una línea.
 *
 * Enseñar el XML crudo truncado no dice nada útil en una tabla: lo que
 * interesa de un vistazo es qué hace. Si conecta audio a un WebSocket, eso es
 * lo que se muestra, porque es el caso para el que existe el markup guardado.
 */
function resumenDeMarkup(doc: string | null): string {
  if (doc === null || doc.trim() === "") return "";
  const stream = /<Stream[^>]*\surl=["']([^"']+)["']/i.exec(doc);
  if (stream?.[1]) return `conecta el audio a ${stream[1]}`;
  const verbos = [...doc.matchAll(/<([A-Z][A-Za-z]*)\b/g)]
    .map((m) => m[1])
    .filter((v) => v !== "Response");
  return verbos.length > 0 ? verbos.join(" → ") : doc.trim().slice(0, 60);
}

/**
 * EditForm cambia lo que se puede cambiar de una aplicación ya creada.
 *
 * Separado del alta porque las dos operaciones no piden lo mismo: al crear hay
 * que elegir de dónde salen las instrucciones, y al editar lo que se suele
 * venir a tocar es a dónde avisar cuando la llamada termina.
 *
 * El markup y la Voice URL también se editan aquí, pero la fuente no se cambia
 * desde esta pantalla: pasar de una a otra es una decisión distinta a corregir
 * una URL, y mezclarlas haría muy fácil vaciar sin querer lo que hay escrito.
 */
function EditForm({
  app,
  onCancel,
  onSaved,
}: {
  app: VoiceApp;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(app.name);
  const [voiceURL, setVoiceURL] = useState(app.voice_url ?? "");
  const [markupDoc, setMarkupDoc] = useState(app.markup ?? "");
  const [callbackURL, setCallbackURL] = useState(app.status_callback_url ?? "");
  const [eventos, setEventos] = useState<string[]>(app.status_callback_events ?? []);
  const [grabar, setGrabar] = useState(app.recording_enabled);
  const [status, setStatus] = useState(app.status);
  const [amd, setAmd] = useState<AMDConfig>(amdDe(app));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const cuerpo: Record<string, unknown> = {
        name,
        status,
        // Vacío significa «quítalo», y por eso va como null y no como "".
        status_callback_url: callbackURL.trim() === "" ? null : callbackURL.trim(),
        status_callback_events: eventos,
        recording_enabled: grabar,
        ...amd,
      };
      if (app.source === "markup") cuerpo.markup = markupDoc;
      else cuerpo.voice_url = voiceURL;

      await api.patch(`/api/v1/voice-apps/${app.id}`, cuerpo);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo guardar");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={app.name}
      description={
        app.source === "markup"
          ? "Markup guardado: la plataforma lo ejecuta sin salir a la red."
          : "Voice URL: la plataforma consulta tu servidor en cada llamada."
      }
      width="lg"
      onClose={onCancel}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Nombre">
          <input
            required
            maxLength={120}
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        {app.source === "markup" ? (
          <div>
            <MarkupEditor value={markupDoc} onChange={setMarkupDoc} rows={9} />
          </div>
        ) : (
          <Field label="Voice URL" hint="Debe ser https y resolver a una dirección pública.">
            <input
              required
              type="url"
              className={inputClass}
              value={voiceURL}
              onChange={(e) => setVoiceURL(e.target.value)}
            />
          </Field>
        )}

        <div className="border-t border-white/10 pt-4">
          <Field
            label="Avisar a esta URL"
            hint="Los eventos de las llamadas de esta aplicación llegan aquí, firmados. Es aparte de los endpoints de la cuenta: esto es «avísame de lo de esta aplicación»."
          >
            <input
              type="url"
              className={inputClass}
              placeholder="https://tu-servidor.com/eventos"
              value={callbackURL}
              onChange={(e) => setCallbackURL(e.target.value)}
            />
          </Field>

          <fieldset className="mt-4">
            <legend className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Qué eventos
            </legend>
            <p className="mt-1 text-xs text-slate-500">
              Sin ninguno marcado recibe <strong>todos</strong>. Para «avísame cuando termine»,
              basta con <code className="text-xs">call.completed</code>.
            </p>
            <div className="mt-2 grid gap-1 sm:grid-cols-3">
              {EVENTOS_DE_LLAMADA.map((tipo) => (
                <label key={tipo} className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={eventos.includes(tipo)}
                    onChange={(e) =>
                      setEventos((actual) =>
                        e.target.checked ? [...actual, tipo] : actual.filter((t) => t !== tipo),
                      )
                    }
                  />
                  <code className="text-xs">{tipo}</code>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="border-t border-white/10 pt-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={grabar}
              onChange={(e) => setGrabar(e.target.checked)}
            />
            <span className="text-sm text-slate-300">
              <strong className="text-slate-100">Grabar las llamadas</strong>
              <span className="mt-1 block text-xs text-slate-500">
                Mezcla los dos lados en un WAV. Cuando la llamada termina, el aviso lleva{" "}
                <code className="text-xs">recording_sid</code> y una liga firmada con caducidad.
              </span>
            </span>
          </label>
          {grabar && (
            <p className="mt-3 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              El aviso a la otra parte lo tienes que dar tú. La plataforma no lo añade: pon un{" "}
              <code className="text-xs">&lt;Say&gt;</code> al principio de tu documento si tu
              caso lo exige.
            </p>
          )}
        </div>

        <DeteccionDeContestadora valor={amd} onChange={setAmd} />

        <Field label="Estado" hint="Una aplicación deshabilitada deja de recibir avisos.">
          <select
            className={inputClass}
            value={status}
            onChange={(e) => setStatus(e.target.value === "disabled" ? "disabled" : "active")}
          >
            <option value="active">Activa</option>
            <option value="disabled">Deshabilitada</option>
          </select>
        </Field>

        {error !== null && (
          <p className="rounded-md bg-rose-500/10 px-3 py-2 text-sm text-rose-200" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap gap-2 border-t border-white/10 pt-4">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Los de llamada. `stream.*` y `balance.low` existen, pero no son de una
// aplicación concreta: suscribirlos aquí confundiría más que ayudar.
const EVENTOS_DE_LLAMADA = [
  "call.queued",
  "call.initiated",
  "call.progressing",
  "call.ringing",
  "call.answered",
  "call.completed",
  "call.failed",
];

/**
 * DeteccionDeContestadora es la configuración de AMD de la aplicación.
 *
 * # Por qué está aquí y no en el alta
 *
 * Nadie afina nueve parámetros al crear una aplicación: se afinan **con
 * datos**, mirando por qué una campaña clasificó mal. El alta queda simple y
 * esto aparece cuando ya hay algo que corregir.
 *
 * # Por qué los parámetros van plegados
 *
 * Lo que casi todo el mundo necesita es encender la detección y decidir qué
 * hacer ante un buzón. Los nueve de abajo son de quien ya midió y sabe cuál
 * mover; enseñarlos siempre haría que la pantalla pareciera exigir nueve
 * decisiones para usar una casilla.
 */
function DeteccionDeContestadora({
  valor,
  onChange,
}: {
  valor: AMDConfig;
  onChange: (v: AMDConfig) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const activa = valor.machine_detection !== "off";

  const numero = (campo: keyof AMDConfig, etiqueta: string, ayuda?: string) => (
    <Field key={campo} label={etiqueta} hint={ayuda}>
      <input
        type="number"
        className={inputClass}
        value={String(valor[campo])}
        min={1}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange({ ...valor, [campo]: Number.isFinite(n) ? n : valor[campo] });
        }}
      />
    </Field>
  );

  return (
    <div className="border-t border-white/10 pt-4">
      <Field
        label="Detección de contestadora"
        hint="Analiza los primeros segundos para saber si contestó una persona o un buzón."
      >
        <select
          className={inputClass}
          value={valor.machine_detection}
          onChange={(e) =>
            onChange({ ...valor, machine_detection: e.target.value as AMDConfig["machine_detection"] })
          }
        >
          <option value="off">Apagada</option>
          <option value="enable">Analizar</option>
          <option value="detect_message_end">Analizar y esperar al final del saludo</option>
        </select>
      </Field>

      {activa && (
        <>
          <p className="mt-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            Cuesta silencio: quien contesta no oye nada durante el análisis, hasta{" "}
            {(valor.amd_total_analysis_ms / 1000).toFixed(1)} s.
          </p>

          <div className="mt-3">
            <Field label="Ante un buzón" hint="Solo aplica cuando la detección concluye que es una máquina.">
              <select
                className={inputClass}
                value={valor.machine_policy}
                onChange={(e) =>
                  onChange({ ...valor, machine_policy: e.target.value as AMDConfig["machine_policy"] })
                }
              >
                <option value="hangup">Colgar</option>
                <option value="continue">Seguir con el documento</option>
                <option value="continue_and_notify">Seguir y avisar a mi servidor</option>
              </select>
            </Field>
          </div>

          <p className="mt-2 text-xs text-slate-500">
            Si el análisis no oye nada, la llamada <strong>no</strong> se cuelga: un silencio no es
            un buzón, y con algunos carriers el audio tarda en llegar tras contestar.
          </p>

          <button
            type="button"
            className="mt-3 text-xs text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
            onClick={() => setAbierto((v) => !v)}
          >
            {abierto ? "Ocultar los parámetros" : "Afinar los parámetros"}
          </button>

          {abierto && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {numero(
                "amd_initial_silence_ms",
                "Silencio inicial (ms)",
                "Cuánto se espera a la primera palabra. Súbelo si tu carrier entrega el audio tarde.",
              )}
              {numero(
                "amd_max_words",
                "Palabras máximas",
                "A partir de aquí se da por buzón. Con 2, «bueno, ¿quién habla?» ya cuenta como máquina.",
              )}
              {numero("amd_greeting_ms", "Saludo máximo (ms)")}
              {numero("amd_after_greeting_silence_ms", "Silencio tras el saludo (ms)")}
              {numero("amd_total_analysis_ms", "Análisis total (ms)")}
              {numero("amd_min_word_ms", "Palabra mínima (ms)")}
              {numero("amd_between_words_silence_ms", "Silencio entre palabras (ms)")}
              {numero("amd_max_word_ms", "Palabra máxima (ms)")}
              {numero("amd_silence_threshold", "Umbral de silencio")}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** amdDe recorta la configuración de AMD de una aplicación. */
function amdDe(app: VoiceApp): AMDConfig {
  return {
    machine_detection: app.machine_detection,
    machine_policy: app.machine_policy,
    amd_initial_silence_ms: app.amd_initial_silence_ms,
    amd_greeting_ms: app.amd_greeting_ms,
    amd_after_greeting_silence_ms: app.amd_after_greeting_silence_ms,
    amd_total_analysis_ms: app.amd_total_analysis_ms,
    amd_min_word_ms: app.amd_min_word_ms,
    amd_between_words_silence_ms: app.amd_between_words_silence_ms,
    amd_max_words: app.amd_max_words,
    amd_silence_threshold: app.amd_silence_threshold,
    amd_max_word_ms: app.amd_max_word_ms,
  };
}
